/**
 * The Touch detail export's columns, shared by Touch detail's own Copy / CSV and the
 * Touches sheet of Claim detail's "Excel + touches" workbook (review #968), so the two
 * never drift apart.
 */
import type { ExportColumn } from '@/ui/export'
import type { TouchDetailData } from '@/data/touchDetail'
import { patientOf } from '@/data/patient'

/** "2026-03-01" -> "3/1/2026", as the Ledger shows it. */
export const usDate = (v: string) =>
  /^\d{4}-\d{2}-\d{2}/.test(v) ? `${+v.slice(5, 7)}/${+v.slice(8, 10)}/${v.slice(0, 4)}` : v

/** One column per field Touch detail exports, over row indexes into `data`. */
export function touchExportColumns(
  data: TouchDetailData,
  jobTitleOf: (employee: string) => string,
): ExportColumn<number>[] {
  const { dict, cols } = data
  const txt = (key: Exclude<keyof typeof dict, 'o' | 'rm' | 'bm' | 'pt' | 'wr'>) => (i: number) =>
    dict[key][cols[key][i]!] ?? ''
  const patient = patientOf(data)
  return [
    { header: 'Employee', value: txt('e') },
    { header: 'Job title', value: (i) => jobTitleOf(txt('e')(i)) },
    { header: 'Facility group', value: txt('fg') },
    { header: 'Facility', value: txt('f') },
    { header: 'State', value: txt('s') },
    ...(patient ? [{ header: 'Patient', value: patient }] : []),
    { header: 'Visit', value: (i) => cols.v[i] ?? '' },
    { header: 'Date', value: (i) => usDate(txt('d')(i)) },
    { header: 'Worked from', value: txt('wf') },
    { header: 'Worklist', value: txt('wl') },
    { header: 'Payer type', value: txt('p') },
    { header: 'Payer name', value: txt('pn') },
    ...(cols.w
      ? [
          { header: 'Weight', value: (i: number) => cols.w![i] ?? 1 },
          { header: 'Weighted because', value: (i: number) => (cols.wr && dict.wr?.[cols.wr[i]!]) || '' },
        ]
      : []),
    { header: 'Status', value: txt('sd') },
    { header: 'Action', value: txt('ad') },
    { header: 'Note', value: (i) => cols.note[i] ?? '' },
  ]
}
